/*
| ---------------
| Base Vue setup 
| ---------------
| Setup the Vue instance, router and Vuex store manager.
*/
// IE supported polyfill for object entries
if (!Object.entries) {
  Object.entries = function(obj) {
    var ownProps = Object.keys(obj),
      i = ownProps.length,
      resArray = new Array(i) // preallocate the Array
    while (i--) resArray[i] = [ownProps[i], obj[ownProps[i]]]

    return resArray
  }
}

import Vue from 'vue'
import App from '@/App.vue'
import router from '@/router'
import store from '@/store/index'
import moment from 'moment-timezone'
import axios from 'axios'

import '@/registerServiceWorker'
import '@/utils/mutators'
require('core-js')


// set momentjs locale
// Date time instances
require('moment/locale/en-gb')
require('moment/locale/nl')
require('moment/locale/fr')

moment.locale(store.getters.language)

// translations
import VueI18n from 'vue-i18n'
Vue.use(VueI18n)
import Messages from './translations/index'

const i18n = new VueI18n({
  // locale: navigator.language, // set locale
  locale: store.getters.language, // set locale
  fallbackLocale: 'en',
  messages: Messages, // set locale messages
  silentTranslationWarn: false
})

import Toasted from 'vue-toasted'

Vue.use(Toasted, {
  theme: 'outline',
  position: 'bottom-right',
  duration: 5000,
  action: {
    text: 'Close',
    onClick: (e, toastObject) => {
      toastObject.goAway(0)
    }
  }
})
/*
| ---------------
| Base Vengo auth 
| ---------------
| Setup the token request and response handlers
*/

const base = axios.create({
  baseURL: process.env.VUE_APP_API_URL,
  headers: {
    Accept: 'application/json',
    'Content-Type': 'application/json',
    'Api-Version': process.env.VUE_APP_API_VERSION,
    'Accept-Language': i18n.locale
  }
})

let authTokenRequest = null

function getAuthToken() {
  // check if the current store token expires soon
  if (store.getters.access_token && store.getters.expires_in - 300 <= moment().unix()) {
    // if not already requesting a token
    if (authTokenRequest === null) {
      // refresh
      authTokenRequest = base
        .post('/oauth/access_token', {
          grant_type: 'refresh_token',
          client_id: process.env.VUE_APP_CLIENT_ID,
          client_secret: process.env.VUE_APP_CLIENT_SECRET,
          refresh_token: store.getters.refresh_token,
          scope: 'app'
        })
        .then(resp => {
          let expires = moment()
            .add(resp.data.expires_in, 'seconds')
            .unix()
          let token = resp.data.access_token + ';' + resp.data.refresh_token + ';' + expires
          localStorage.setItem('token', token)
          resetAuthTokenRequest()
          store.commit('auth/AUTH_SUCCESS', token)
          return resp.data.access_token
        })
        .catch(err => {
          store.commit('auth/AUTH_ERROR')
        })
    }
    return authTokenRequest
  } else {
    return store.getters.access_token
  }
}

// tokenRequest dirty bit reseter
function resetAuthTokenRequest() {
  authTokenRequest = null
}

// Axios Intercept Requests
base.interceptors.request.use(
  async function(config) {
    let uri = config.url.replace(config.baseURL, '')
    // If not one of these specific pages that doesn't need a token, use method to get the current token or request a new one.  Otherwise, use current token (possibly none)

    if (!['/oauth/access_token', '/api/emailexists', '/api/company'].includes(uri)) {
      config.headers['Authorization'] = 'Bearer ' + (await getAuthToken())
    }

    return config
  },
  function(error) {
    return Promise.reject(error)
  }
)

base.interceptors.response.use(
  function(config) {
    return config        
  },
  function(error) {

    // Prevent endless redirects (login is where you should end up)
    if (error.request !== undefined) {
      if (error.request.responseURL.includes('login')) {
        return Promise.reject(error)
      }
    }

    // If you can't refresh your token or you are sent Unauthorized on any request, logout and go to login
    if (
      error.request !== undefined &&
      (error.request.responseURL.includes('access_token') ||
        (error.request.status === 401 && error.config.__isRetryRequest))
    ) {
      store.commit('fleet/RESET_FLEET')
      store.commit('chat/RESET_CHAT')
      store.commit('shipments/RESET_SHIPMENTS')
      store.commit('auth/RESET_AUTH')
      router.push({ name: 'login' })
    } else if (error.request !== undefined && error.request.status === 401) {
      error.config.__isRetryRequest = true
      return base.request(error.config)
    }

    if (error.response && error.response.data) {

      let message = false;

      if (error.response.data.hasOwnProperty('message') ) {
        message = error.response.data.message;
      }

      if (Array.isArray(error.response.data) && error.response.data.length > 0 && error.response.data[0].hasOwnProperty('message')) {
        message = error.response.data[0].hasOwnProperty('message');
      }

      if(message) {
        Vue.toasted.show(message);
      } 
    }
    
    return Promise.reject(error)
  }
)

Vue.prototype.$http = base

/*
| ---------------
| Base Vengo styling 
| ---------------
| Setup the Scss styling, icons and variables
*/
import '@/styles/app.scss'
import '@/assets/icons/index.js'

/*
| ---------------
| Base Vengo layouts 
| ---------------
| Setup the page and box layout
*/
import Box from '@/layouts/box'
Vue.component('box', Box)
import Slideout from '@/layouts/slideout'
Vue.component('slideout', Slideout)
import Popup from '@/layouts/popup'
Vue.component('popup', Popup)

/*
| ---------------
| Base Vengo plugins 
| ---------------
| Setup global plugins used in vengo fleet
| VeeValidate, Vue-moment
*/

// profile image
import ProfileImage from '@/components/elements/profile-image'
Vue.component('profile-image', ProfileImage)

// Custom directive
// vue-click-outside
// When clicking somewhere except a the dom element perform the given method
Vue.directive('click-outside', {
  bind: function(el, binding, vnode) {
    el.event = function(event) {
      if (!(el === event.target || el.contains(event.target))) {
        if (typeof binding.value === 'function') {
          binding.value.bind(vnode.context)(event)
        } else {
          let expression = ''
          let beg = ["'", '`', '"'].includes(binding.expression[0]) ? 1 : 0
          let count = ["'", '`', '"'].includes(binding.expression[binding.expression.length - 1])
            ? binding.expression.length - 1
            : binding.expression.length
          expression = binding.expression.substring(beg, count)
          new Function(expression).bind(vnode.context)(event)
        }
      }
    }
    document.body.addEventListener('click', el.event)
  },
  unbind: function(el) {
    document.body.removeEventListener('click', el.event)
  }
})

// Form validation
import VeeValidate from 'vee-validate'
import { Validator } from 'vee-validate'
import enValidationMessages from 'vee-validate/dist/locale/en'
import nlValidationMessages from 'vee-validate/dist/locale/nl'
import frValidationMessages from 'vee-validate/dist/locale/fr'

Vue.use(VeeValidate, {
  i18nRootKey: 'validations',
  i18n,
  dictionary: {
    en: enValidationMessages,
    fr: frValidationMessages,
    nl: nlValidationMessages
  }
})

Validator.localize({
  en: {
    messages:{
      email_exists: () => 'This e-mail address already exists.'
    }
  },
  nl: {
    messages: {
      email_exists: () => 'Dit e-mailadres bestaat al.'
    }
  },
  fr: {
    messages: {
      email_exists: () => 'Frans'
    }
  }
});

Validator.extend('email_exists', {
  // messages: field => 'Dit e-mailadres is al in gebruik',
  validate: value => {
    return base
      .post('/oauth/access_token', {
        grant_type: 'client_credentials',
        client_id: process.env.VUE_APP_CLIENT_ID,
        client_secret: process.env.VUE_APP_CLIENT_SECRET,
        scope: 'app'
      })
      .then(response => {
        return base
          .post(
            '/api/emailexists',
            { email: value },
            { headers: { Authorization: 'Bearer ' + response.data.access_token } }
          )
          .then(response => {
            return !response.data.exists
          })
          .catch(error => {
            return error
          })
      })
      .catch(error => {
        return error
      })
  }
})



Vue.use(require('vue-moment'), { moment })

// Google maps support
import * as VueGoogleMaps from 'vue2-google-maps'
Vue.use(VueGoogleMaps, {
  load: {
    key: 'AIzaSyDSBUezuBgMkryBu7wIKz33kiQU--sN5ts',
    libraries: 'places, geocode'
  }
})

// Date range picker support
import AirbnbStyleDatepicker from 'vue-airbnb-style-datepicker'
import 'vue-airbnb-style-datepicker/dist/vue-airbnb-style-datepicker.min.css'
Vue.use(AirbnbStyleDatepicker)

// General vengo loading animations
import Loader from '@/components/global/loader'
Vue.component('loader', Loader)

import Disclaimer from '@/components/elements/disclaimer'
Vue.component('disclaimer', Disclaimer)

Vue.config.productionTip = false

new Vue({
  router,
  store,
  i18n,
  render: h => h(App),
}).$mount('#app')
